// Bi-directional bridge between the structured `blocks[]` used by the editor
// and the markdown-lite `content` string that the front-office renderer
// consumes. Keeping the SAME grammar the backend understands means the
// editor round-trips losslessly for anything it can express.
//
// Blocks shape:
//   { id, type: "paragraph" | "subtitle" | "pull_quote" | "list" | "image",
//     text?: string,      // for paragraph/subtitle/pull_quote (may include \n)
//     items?: string[],   // for list
//     image_id?: string, image_url?: string, image_alt?: string }
//
// The `id` is client-side only (React keys). It's stripped before sending
// to the server.

let _seq = 0;
export const newBlockId = () => `b-${Date.now().toString(36)}-${(_seq++).toString(36)}`;

export const emptyBlock = (type = "paragraph") => {
    switch (type) {
        case "subtitle": return { id: newBlockId(), type, text: "" };
        case "pull_quote": return { id: newBlockId(), type, text: "" };
        case "signature": return { id: newBlockId(), type, text: "" };
        case "list": return { id: newBlockId(), type, items: [""] };
        case "image": return { id: newBlockId(), type, image_id: "", image_url: "", image_alt: "" };
        default: return { id: newBlockId(), type: "paragraph", text: "" };
    }
};

/* Compile blocks → markdown-lite string. Empty blocks collapse to nothing
   so the preview and the final saved content stay identical. */
export function blocksToContent(blocks) {
    if (!Array.isArray(blocks) || !blocks.length) return "";
    const chunks = [];
    for (const b of blocks) {
        if (!b || !b.type) continue;
        if (b.type === "paragraph") {
            const t = (b.text || "").replace(/\r\n/g, "\n").trim();
            if (t) chunks.push(t);
        } else if (b.type === "subtitle") {
            const t = (b.text || "").replace(/\r\n/g, "\n").trim();
            if (t) chunks.push(`## ${t}`);
        } else if (b.type === "pull_quote") {
            const t = (b.text || "").replace(/\r\n/g, "\n").trim();
            if (t) {
                // Pull-quotes may span multiple lines — prefix EACH one.
                chunks.push(t.split("\n").map((l) => `!! ${l}`).join("\n"));
            }
        } else if (b.type === "signature") {
            // Signature/closing block — newsletter template renders this
            // as a bordered italic block. Prefix each line with `!sig `.
            const t = (b.text || "").replace(/\r\n/g, "\n").trim();
            if (t) {
                chunks.push(t.split("\n").map((l) => `!sig ${l}`).join("\n"));
            }
        } else if (b.type === "list") {
            const items = (b.items || []).map((it) => (it || "").trim()).filter(Boolean);
            if (items.length) chunks.push(items.map((it) => `- ${it}`).join("\n"));
        } else if (b.type === "image") {
            const src = b.image_id || b.image_url || "";
            if (src) chunks.push(`![${(b.image_alt || "").replace(/[\[\]()]/g, "")}](${src})`);
        }
    }
    return chunks.join("\n\n");
}

/* Legacy migration: parse markdown-lite string into blocks so posts written
   with the old textarea open cleanly in the block editor. Parsing is a
   best-effort inverse: unknown patterns fall back to paragraph. Never
   destructive — the raw `content` is preserved on the server until save. */
const IMG_RE = /^!\[([^\]]*)\]\(([^)]+)\)\s*$/;

export function parseContentToBlocks(content) {
    if (!content || typeof content !== "string") return [emptyBlock("paragraph")];
    const parts = content.replace(/\r\n/g, "\n").split(/\n\s*\n/);
    const blocks = [];
    for (const raw of parts) {
        const lines = raw.split("\n").filter((l) => l.length);
        if (!lines.length) continue;
        // Image on its own line.
        if (lines.length === 1 && IMG_RE.test(lines[0])) {
            const [, alt, src] = IMG_RE.exec(lines[0]);
            const looksLikeUrl = /^https?:\/\/|^\/api\/files\//.test(src);
            blocks.push({
                id: newBlockId(), type: "image",
                image_id: looksLikeUrl ? "" : src,
                image_url: looksLikeUrl ? src : "",
                image_alt: alt || "",
            });
            continue;
        }
        // H2
        if (lines.length === 1 && /^##\s+/.test(lines[0])) {
            blocks.push({ id: newBlockId(), type: "subtitle", text: lines[0].replace(/^##\s+/, "") });
            continue;
        }
        // H3 → treated as subtitle in the editor (legacy grammar level).
        if (lines.length === 1 && /^###\s+/.test(lines[0])) {
            blocks.push({ id: newBlockId(), type: "subtitle", text: lines[0].replace(/^###\s+/, "") });
            continue;
        }
        // Pull-quote (one or more lines, all prefixed with !!).
        if (lines.every((l) => /^!!\s+/.test(l))) {
            blocks.push({
                id: newBlockId(), type: "pull_quote",
                text: lines.map((l) => l.replace(/^!!\s+/, "")).join("\n"),
            });
            continue;
        }
        // Signature block (all lines prefixed with `!sig `).
        if (lines.every((l) => /^!sig\s+/.test(l))) {
            blocks.push({
                id: newBlockId(), type: "signature",
                text: lines.map((l) => l.replace(/^!sig\s+/, "")).join("\n"),
            });
            continue;
        }
        // List (all lines prefixed with `- `).
        if (lines.every((l) => /^-\s+/.test(l))) {
            blocks.push({
                id: newBlockId(), type: "list",
                items: lines.map((l) => l.replace(/^-\s+/, "")),
            });
            continue;
        }
        // Fallback → paragraph. Multi-line paragraphs (single \n between lines)
        // are preserved so the front-office renderer keeps <br /> behaviour.
        blocks.push({ id: newBlockId(), type: "paragraph", text: lines.join("\n") });
    }
    return blocks.length ? blocks : [emptyBlock("paragraph")];
}

/* Strip client-side `id` from blocks before sending to the server. */
export function serializeBlocks(blocks) {
    if (!Array.isArray(blocks)) return [];
    return blocks
        .map((b) => {
            const out = { type: b.type };
            if (b.type === "list") out.items = (b.items || []).map((s) => s || "");
            else if (b.type === "image") {
                out.image_id = b.image_id || "";
                out.image_url = b.image_url || "";
                out.image_alt = b.image_alt || "";
            } else out.text = b.text || "";
            return out;
        })
        .filter((b) => {
            if (b.type === "list") return (b.items || []).some((it) => (it || "").trim());
            if (b.type === "image") return !!(b.image_id || b.image_url);
            return !!(b.text || "").trim();
        });
}
